/**
 * protocolCompare.js — сопоставимы ли две съёмки между собой (ядро, чистое).
 *
 * ЗАЧЕМ ЭТО ОТДЕЛЬНО ОТ КАЧЕСТВА КАЖДОЙ. Два исследования могут быть каждое
 * безупречным по своему протоколу и при этом несравнимыми между собой. Июль
 * снят срезом 1 мм, август — 3 мм: обе съёмки хорошие, но объём очага,
 * измеренный на них, отличается сам по себе, без всякой опухоли. Часть
 * найденного «роста» окажется от толщины среза.
 *
 * Измерено, а не предположено: один и тот же шар диаметром 17 мм, нарезанный
 * с шагом 1 мм и с шагом 3 мм, меряется как 2448 и 2269 мм³ — расхождение
 * около 7% на ровном месте, без всякой опухоли (protocol_study_test, раздел 6).
 *
 * Число здесь названо ИМЕННО ТО, что измерено. Сперва я написал в
 * предупреждении «больше 20%» — цифру, взятую из головы до измерения, — и
 * тест это поймал. Поэтому она вынесена в константу, а тест сверяет её со
 * своим свежим замером: разойтись молча они больше не могут.
 *
 * Модуль чистый: на вход два разбора серии, на выход — вердикт. Поэтому
 * правила проверяются тестом, а не разглядыванием экрана.
 */

/**
 * Насколько сама толщина среза смещает измеренный объём очага, в процентах.
 * Значение получено замером на шаре 17 мм (срез 1 мм против 3 мм) и сверяется
 * тестом. Менять руками нельзя — только вслед за новым замером.
 */
export const СМЕЩЕНИЕ_ОТ_СРЕЗА_ПРОЦ = 7;

const ЧИСЛО = (v) => (Number.isFinite(v) ? v : null);

/** Насколько две толщины среза расходятся, в долях от меньшей. */
function _расхождение(a, b) {
  const x = ЧИСЛО(a), y = ЧИСЛО(b);
  if (x == null || y == null || x <= 0 || y <= 0) return null;
  return Math.abs(x - y) / Math.min(x, y);
}

const НАЗВАНИЕ = {
  t1: 'T1', t2: 'T2', flair: 'FLAIR', dwi: 'DWI', mra: 'MRA/MRV',
  ssfp: 'FIESTA/CISS', swi: 'SWI/T2*', scout: 'скаут', other: '—',
};

/**
 * Сравнить две серии как пару для динамики.
 *
 * @param {Object} a — разбор серии первого исследования (analyzeSeries)
 * @param {Object} b — разбор серии второго
 * @returns {{сопоставимы: boolean, уровень: 'ok'|'warn'|'fail',
 *            замечания: string[], словами: string}}
 */
export function compareForFollowUp(a, b, o = {}) {
  const имяА = o.имяА || 'первая', имяБ = o.имяБ || 'вторая';
  if (!a || !b) {
    return { сопоставимы: false, уровень: 'warn', замечания: ['нет второй съёмки'],
      словами: 'Сравнивать не с чем — заполните оба окна' };
  }

  const замечания = [];
  let уровень = 'ok';
  const хуже = (l) => { if (l === 'fail' || (l === 'warn' && уровень === 'ok')) уровень = l; };

  // 1. Контраст. Самое опасное расхождение и притом самое незаметное: обе
  // съёмки выглядят нормальными T1, а на карте разницы вся опухоль вспыхнет
  // как «выросшая» — просто потому, что на одной она накопила контраст.
  // [12.0.8] Слова владельца (по снимку экрана): «справа МРТ с контрастом, а
  // твой код пишет, что без». Текст был безадресным — «первая/вторая» по
  // порядку аргументов, который врач на экране не видит. Теперь вызывающий
  // передаёт имена сторон КАК ИХ ВИДИТ ВРАЧ («в левом окне»/«в правом окне»),
  // и предупреждение называет окно, а не порядковый номер.
  if (a.hasContrast !== b.hasContrast) {
    const [сК, безК] = a.hasContrast ? [имяА, имяБ] : [имяБ, имяА];
    замечания.push(`съёмка ${сК} с контрастом, ${безК} — без; карта разницы на такой паре бессмысленна, `
      + 'а объём очага будет измерен по разным границам');
    хуже('fail');
  }

  // 2. Взвешенность. T1 против T2 — это разные физические величины, вычитать
  // их друг из друга нельзя даже после приведения яркостей.
  if (a.kind && b.kind && a.kind !== b.kind) {
    if (a.kind === 'other' || b.kind === 'other') {
      // [10.19.1] «Не распознана по тегам» — не то же, что «другая». Раньше
      // нечитаемые теги давали категоричный запрет «разная взвешенность: —
      // против T1». Не знаем — говорим, что не знаем, и просим сверить глазом.
      замечания.push('взвешенность одной из съёмок не распознана по тегам — сверьте глазом, что обе однотипные');
      хуже('warn');
    } else {
      замечания.push(`разная взвешенность: ${НАЗВАНИЕ[a.kind] || a.kind} против ${НАЗВАНИЕ[b.kind] || b.kind} `
        + '— сравнивать в динамике можно только однотипные съёмки');
      хуже('fail');
    }
  }

  // 3. Толщина среза. Число в предупреждении — измеренное (см. заголовок).
  const р = _расхождение(a.thk, b.thk);
  if (р != null && р > 0.2) {
    замечания.push(`толщина среза ${Number(a.thk).toFixed(1)} мм против ${Number(b.thk).toFixed(1)} мм `
      + '— часть найденного роста будет от толщины среза, а не от опухоли '
      + `(замерено на шаре 17 мм для пары 1/3 мм: около ${СМЕЩЕНИЕ_ОТ_СРЕЗА_ПРОЦ}%; при большей разнице смещение больше)`);
    хуже('warn');
  } else if (р == null) {
    замечания.push('толщина среза известна не для обеих съёмок — судить о сопоставимости не берусь');
    хуже('warn');
  }

  // 4. Плоскость. Не запрет: программа умеет реформаты, — но предупредить надо.
  if (a.plane && b.plane && a.plane !== b.plane) {
    замечания.push(`съёмки в разных плоскостях (${a.plane} и ${b.plane}) — совмещение это переживёт, `
      + 'но разрешение вдоль срезов у них разное');
    хуже('warn');
  }

  // 5. Интервал между срезами.
  const зазор = (v) => (Number.isFinite(v) && v > 0.15);
  if (зазор(a.gap) || зазор(b.gap)) {
    замечания.push('между срезами есть зазор — в промежутки очаг может просто не попасть');
    хуже('warn');
  }

  const сопоставимы = уровень === 'ok';
  const словами = сопоставимы
    ? 'Съёмки сопоставимы: одинаковая взвешенность, контраст и толщина среза'
    : (уровень === 'fail'
      ? 'Сравнивать нельзя: ' + замечания.join('; ')
      : 'Сравнивать с осторожностью: ' + замечания.join('; '));

  return { сопоставимы, уровень, замечания, словами };
}


/**
 * [12.0.8] Серия по умолчанию для окна сравнения — из СВОДОК серий protocolCheck.
 * Правило прежнее «самая длинная» ПЛЮС отсев мусора: скауты (isLocalizer) и
 * производные (isDerived) в умолчание не годятся — обзорник в 15 кадров с
 * толщиной 5 мм не пара рабочей съёмке, даже когда рабочая не загрузилась.
 * Если после отсева не осталось ничего — честно берём длиннейшую из всех
 * (лучше показать скаут, чем пустое окно). Чистая: проверяется тестом.
 */
export function выбратьСериюПоУмолчанию(сводки) {
  const список = (сводки || []).filter((s) => s && s.uid && (s.count || 0) > 0);
  if (!список.length) return '';
  const годные = список.filter((s) => !s.isLocalizer && !s.isDerived);
  const из = годные.length ? годные : список;
  return из.reduce((a, b) => ((b.count || 0) > (a.count || 0) ? b : a)).uid;
}
